import type { Alert, AutopilotAgent, Server } from "@cloudserver/contracts";

/*
 * The rows of the project overview «Мониторинг» (docs/autopilot.md): every
 * server of the project with its Autopilot agent (or none) and its firing
 * alerts, worst first. Pure, so the order and the filters are tested.
 */

export interface MonitoringRow {
  server: Server;
  /** Null when the server has no Autopilot. */
  agent: AutopilotAgent | null;
  alerts: { total: number; critical: number };
}

export const MONITORING_FILTERS = [
  "all",
  "problems",
  "critical",
  "notReporting",
  "off",
  "updates",
] as const;
export type MonitoringFilter = (typeof MONITORING_FILTERS)[number];

/** One server per row, in the servers' order; alerts of the project itself are left out. */
export function buildRows(
  servers: Server[],
  agents: AutopilotAgent[],
  firing: Alert[],
): MonitoringRow[] {
  const agentOf = new Map(agents.map((a) => [a.serverId, a]));
  const alertsOf = new Map<string, { total: number; critical: number }>();
  for (const alert of firing) {
    if (!alert.serverId || alert.status !== "firing") continue;
    const counts = alertsOf.get(alert.serverId) ?? { total: 0, critical: 0 };
    counts.total++;
    if (alert.severity === "critical") counts.critical++;
    alertsOf.set(alert.serverId, counts);
  }
  return servers.map((server) => ({
    server,
    agent: agentOf.get(server.id) ?? null,
    alerts: alertsOf.get(server.id) ?? { total: 0, critical: 0 },
  }));
}

/**
 * How bad a row is, 0 first: critical health or a critical alert; warnings
 * or any firing alert; an agent that does not report yet (waiting,
 * connected, expired); all well; no Autopilot at all.
 */
export function rank(row: MonitoringRow): number {
  const health = row.agent?.health;
  if (health === "critical" || row.alerts.critical > 0) return 0;
  if (health === "warn" || row.alerts.total > 0) return 1;
  if (!row.agent) return 4;
  if (health === "unknown") return 2;
  return 3;
}

/** The value the state column sorts by: rank, then more alerts first. */
export const severityKey = (row: MonitoringRow): number =>
  rank(row) * 1000 - Math.min(999, row.alerts.total);

/** Worst first, then by name: the page's default order. */
export function worstFirst(rows: MonitoringRow[]): MonitoringRow[] {
  return [...rows].sort(
    (a, b) => severityKey(a) - severityKey(b) || a.server.name.localeCompare(b.server.name),
  );
}

/** Name text (server name or IPv4) and the state filter; empty text matches everything. */
export function matches(row: MonitoringRow, text: string, filter: MonitoringFilter): boolean {
  const needle = text.trim().toLowerCase();
  if (
    needle &&
    ![row.server.name, row.server.publicIpv4?.address].some((v) =>
      v?.toLowerCase().includes(needle),
    )
  )
    return false;
  switch (filter) {
    case "all":
      return true;
    case "problems":
      return rank(row) <= 1;
    case "critical":
      return rank(row) === 0;
    case "notReporting":
      return row.agent !== null && row.agent.status !== "reporting";
    case "off":
      return row.agent === null;
    case "updates":
      return (
        row.agent?.updateAvailable === true || (row.agent?.summary?.updates?.security ?? 0) > 0
      );
  }
}

export interface MonitoringTotals {
  servers: number;
  /** Servers with Autopilot. */
  watched: number;
  critical: number;
  warn: number;
  off: number;
  /** Firing alerts of the servers. */
  alerts: number;
  /** Pending security updates over all servers. */
  securityUpdates: number;
  /** Agents with a newer build to install. */
  agentUpdates: number;
}

export function totals(rows: MonitoringRow[]): MonitoringTotals {
  const out: MonitoringTotals = {
    servers: rows.length,
    watched: 0,
    critical: 0,
    warn: 0,
    off: 0,
    alerts: 0,
    securityUpdates: 0,
    agentUpdates: 0,
  };
  for (const row of rows) {
    out.alerts += row.alerts.total;
    const r = rank(row);
    if (r === 0) out.critical++;
    else if (r === 1) out.warn++;
    if (!row.agent) {
      out.off++;
      continue;
    }
    out.watched++;
    out.securityUpdates += row.agent.summary?.updates?.security ?? 0;
    if (row.agent.updateAvailable) out.agentUpdates++;
  }
  return out;
}

/** Containers that need a look: unhealthy, restarting or stopped badly. */
export const containerProblems = (agent: AutopilotAgent | null): number => {
  const c = agent?.summary?.containers;
  return c ? c.unhealthy + c.restarting + c.stopped : 0;
};

/** The 1-minute load per core, the way the `load_high` alert rule judges it. */
export function loadPerCore(row: MonitoringRow): number | null {
  const load = row.agent?.summary?.load?.[0];
  const cores = row.server.type.cores;
  return load === undefined || !cores ? null : load / cores;
}
